import Phaser from 'phaser';
import { LayerDepths } from '../constants/enums';

/**
 * Data structure for object information
 */
export interface ObjectData {
  uid: number | null;
  name: string;
  type: string;
  layerName: string;

  // Transform
  x: number;
  y: number;
  width: number;
  height: number;
  originX: number;
  originY: number;
  angle: number;
  scaleX: number;
  scaleY: number;
  flipX: boolean;
  flipY: boolean;
  depth: number;

  // Visual
  texture: string | null;
  frame: string | number | null;

  // Tilemap info (optional)
  tileCol?: number;
  tileRow?: number;
  tileIndex?: number;

  // Stack info (when multiple objects overlap)
  stackInfo?: string;  // "1/3" means object 1 of 3

  // Custom data
  customData: Record<string, unknown>;

  // Health info (for entities with HealthBehavior)
  hp?: number;
  maxHp?: number;
}

/**
 * ObjectInfoPanel - Displays detailed information about a selected object
 *
 * Shows a panel with object properties like:
 * - Name/Type
 * - UID
 * - Layer
 * - Position, Size, Origin
 * - Texture/Frame
 * - Custom data
 */
export class ObjectInfoPanel {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private background: Phaser.GameObjects.Rectangle;
  private closeButton: Phaser.GameObjects.Text;
  private titleText: Phaser.GameObjects.Text;
  private contentText: Phaser.GameObjects.Text;
  private visible: boolean = false;

  // Panel dimensions
  private panelWidth: number = 420;
  private panelHeight: number = 500;
  private padding: number = 18;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Create container (fixed to camera)
    this.container = scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(LayerDepths.Debug);
    this.container.setVisible(false);

    // Background
    this.background = scene.add.rectangle(
      0, 0,
      this.panelWidth, this.panelHeight,
      0x111111, 0.95
    );
    this.background.setOrigin(0, 0);
    this.background.setStrokeStyle(2, 0x00ffff);
    this.container.add(this.background);

    // Title
    this.titleText = scene.add.text(this.padding, this.padding, '', {
      fontFamily: 'monospace',
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#00ffff',
    });
    this.container.add(this.titleText);

    // Close button
    this.closeButton = scene.add.text(
      this.panelWidth - this.padding, this.padding,
      '[X]',
      {
        fontFamily: 'monospace',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#ff6666',
      }
    );
    this.closeButton.setOrigin(1, 0);
    this.closeButton.setInteractive({ useHandCursor: true });
    this.closeButton.on('pointerover', () => this.closeButton.setColor('#ff9999'));
    this.closeButton.on('pointerout', () => this.closeButton.setColor('#ff6666'));
    this.closeButton.on('pointerdown', () => this.hide());
    this.container.add(this.closeButton);

    // Content area
    this.contentText = scene.add.text(this.padding, this.padding + 32, '', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#cccccc',
      lineSpacing: 8,
      wordWrap: { width: this.panelWidth - this.padding * 2 },
    });
    this.container.add(this.contentText);

    // Position panel in top-right corner
    this.updatePosition();
  }

  /**
   * Shows the panel with object data
   */
  show(data: ObjectData): void {
    this.visible = true;
    this.container.setVisible(true);

    // Update title (with stack info if multiple objects)
    const stackLabel = data.stackInfo ? ` [${data.stackInfo}]` : '';
    this.titleText.setText(`[${data.name}]${stackLabel}`);

    // Build content
    const lines: string[] = [];

    // Stack hint if multiple objects
    if (data.stackInfo) {
      lines.push(`Stack: ${data.stackInfo} (click to cycle)`);
      lines.push('───────────────────────');
    }

    // Basic info
    lines.push(`UID: ${data.uid ?? 'N/A'}`);
    lines.push(`Type: ${data.type}`);
    lines.push(`Layer: ${data.layerName}`);

    // HP info (if entity has health)
    if (data.hp !== undefined && data.maxHp !== undefined) {
      lines.push(`HP: ${data.hp}/${data.maxHp}`);
    }

    lines.push('───────────────────────');

    // Transform
    lines.push(`Position: (${Math.round(data.x)}, ${Math.round(data.y)})`);
    lines.push(`Size: ${Math.round(data.width)} x ${Math.round(data.height)}`);
    lines.push(`Origin: (${data.originX.toFixed(2)}, ${data.originY.toFixed(2)})`);
    lines.push(`Scale: (${data.scaleX.toFixed(2)}, ${data.scaleY.toFixed(2)})`);
    lines.push(`Flip: X=${data.flipX}, Y=${data.flipY}`);
    lines.push(`Angle: ${Math.round(data.angle)}°`);
    lines.push(`Depth: ${data.depth}`);
    lines.push('───────────────────────');

    // Visual
    lines.push(`Texture: ${data.texture ?? 'none'}`);
    if (data.frame !== null && data.frame !== undefined) {
      lines.push(`Frame: ${data.frame}`);
    }

    // Tilemap info
    if (data.tileCol !== undefined && data.tileRow !== undefined) {
      lines.push('───────────────────────');
      lines.push(`Tile Grid: col ${data.tileCol}, row ${data.tileRow}`);
      if (data.tileIndex !== undefined) {
        lines.push(`Tile Index: ${data.tileIndex}`);
      }
    }

    // Custom data
    const customKeys = Object.keys(data.customData).filter(k => k !== 'uid');
    if (customKeys.length > 0) {
      lines.push('───────────────────────');
      lines.push('Custom Data:');
      for (const key of customKeys) {
        const value = data.customData[key];
        const valueStr = typeof value === 'object'
          ? JSON.stringify(value)
          : String(value);
        lines.push(`  ${key}: ${valueStr.substring(0, 30)}`);
      }
    }

    this.contentText.setText(lines.join('\n'));

    // Adjust panel height based on content
    const contentHeight = this.contentText.height + this.padding * 2 + 40;
    this.panelHeight = Math.max(250, Math.min(550, contentHeight));
    this.background.setSize(this.panelWidth, this.panelHeight);

    this.updatePosition();
  }

  /**
   * Hides the panel
   */
  hide(): void {
    this.visible = false;
    this.container.setVisible(false);
  }

  /**
   * Returns whether the panel is visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Updates panel position (top-right corner of screen)
   */
  private updatePosition(): void {
    const margin = 16;
    this.container.setPosition(
      this.scene.scale.width - this.panelWidth - margin,
      margin
    );
  }

  /**
   * Checks if a screen point is inside the panel
   */
  containsPoint(screenX: number, screenY: number): boolean {
    if (!this.visible) return false;

    const px = this.container.x;
    const py = this.container.y;

    return (
      screenX >= px &&
      screenX <= px + this.panelWidth &&
      screenY >= py &&
      screenY <= py + this.panelHeight
    );
  }

  /**
   * Destroys the panel
   */
  destroy(): void {
    this.container.destroy();
  }
}
